import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { MoonMark as Moon } from "@/components/MoonMark";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    let timer;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setOrder(res.data);
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          if (window.fbq) window.fbq("track", "Purchase", { value: res.data.total, currency: "USD" });
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts >= MAX_ATTEMPTS) { setState("failed"); return; }
      timer = setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="payment-result-page">
      {state === "checking" && (
        <>
          <Moon className="w-10 h-10 text-gold mx-auto mb-6 animate-pulse" strokeWidth={1.2} />
          <h1 className="font-serif text-4xl text-cream mb-3">Confirming your indulgence…</h1>
          <p className="font-body text-white/50">One moment while we seal your order.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-6" strokeWidth={1.2} />
          <h1 className="font-serif text-5xl text-cream mb-4">Thank you</h1>
          <p className="font-body text-white/60 mb-2">Your order has been confirmed and is being lovingly prepared.</p>
          {order?.total != null && <p className="font-serif text-2xl text-gold mb-8">{money(order.total)}</p>}
          <p className="font-body text-xs text-white/40 mb-10">A confirmation will be sent to your email. Discreetly, of course.</p>
          <Link to="/shop" className="px-8 py-4 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors" data-testid="continue-shopping-btn">Continue Indulging</Link>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle className="w-14 h-14 text-white/50 mx-auto mb-6" strokeWidth={1.2} />
          <h1 className="font-serif text-4xl text-cream mb-4">We couldn't confirm your payment</h1>
          <p className="font-body text-white/50 mb-8">If you were charged, your order is safe — please contact our concierge.</p>
          <Link to="/cart" className="px-8 py-4 border border-gold/50 text-gold font-body uppercase tracking-[0.2em] text-xs hover:bg-gold/10 transition-colors" data-testid="back-to-cart-btn">Return to Bag</Link>
        </>
      )}
    </div>
  );
}
